/**
 * TripCart Currency System v3.0
 *
 * Single source of truth for currency conversion on Cloudflare cached pages.
 *
 * IMPORTANT: The cookie `currency_code` is the ONLY source of truth.
 * All components must read from and write to this cookie via TCCurrency.
 *
 * How it works:
 * 1. On page load, TCCurrency reads currency from cookie
 * 2. All prices are converted client-side using TCCurrency.format()
 * 3. When user changes currency, TCCurrency updates cookie and notifies all components
 * 4. Components listen to 'tc-currency-changed' event to re-render
 *
 * Usage:
 *   HTML:     <span data-tc-price="100">$100.00</span>
 *   Alpine:   x-text="$currency.format(100)"
 *   JS:       TCCurrency.format(100)
 */

(function () {
    'use strict';

    // =============================================================================
    // COOKIE HELPERS
    // =============================================================================

    function getCookie(name) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (!match) return null;

        try {
            return decodeURIComponent(match[2]);
        } catch (e) {
            // Invalid encoding, return null for safety
            return null;
        }
    }

    function setCookie(name, value, days = 365) {
        const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
        const encodedValue = encodeURIComponent(value);
        // Use Secure flag in production (HTTPS), SameSite=Lax for CSRF protection
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${name}=${encodedValue}; expires=${expires}; path=/; SameSite=Lax${secure}`;
    }

    // =============================================================================
    // NUMBER FORMATTING HELPERS
    // =============================================================================

    function truncateNumber(num, decimals) {
        const factor = Math.pow(10, decimals);
        return Math.floor(num * factor) / factor;
    }

    function formatNumber(num, decimals, decimalSep, thousandSep) {
        const fixed = num.toFixed(decimals);
        const parts = fixed.split('.');

        // Add thousand separators
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);

        return decimals > 0 ? parts.join(decimalSep) : parts[0];
    }

    // =============================================================================
    // MAIN MODULE
    // =============================================================================

    const TCCurrency = {
        // State
        config: null,
        currency: null,      // Current currency code (e.g., 'USD', 'EUR')
        rateData: null,      // Current currency's rate data
        ready: false,        // Is the module initialized?
        _observer: null,     // MutationObserver reference for cleanup
        _observerTimeout: null, // Debounce timeout reference

        // -------------------------------------------------------------------------
        // INITIALIZATION
        // -------------------------------------------------------------------------

        /**
         * Initialize the currency system
         * @param {Object} config - Server-provided configuration
         */
        init(config) {
            if (this.ready) return;

            this.config = config;

            // Currency code selection priority:
            // 1. Cookie (user's explicit choice)
            // 2. Server currency (from geolocation or default)
            // 3. Base currency (fallback)
            const cookieCurrency = getCookie('currency_code');

            if (cookieCurrency && this._isValidCurrency(cookieCurrency)) {
                // User has explicitly selected a currency
                this.currency = cookieCurrency;
            } else if (config.serverCurrency && this._isValidCurrency(config.serverCurrency)) {
                // Use server's currency (geolocation or default)
                this.currency = config.serverCurrency;
                // Set cookie so user's preference is remembered
                setCookie('currency_code', this.currency);
            } else {
                // Fallback to base currency
                this.currency = config.baseCurrency;
                setCookie('currency_code', this.currency);
            }

            this.rateData = this._getRateData(this.currency);
            this.ready = true;

            // Convert all static prices on the page (replaces loading placeholder)
            this.convertAllPrices();

            // Watch for dynamically added prices
            this._watchForNewPrices();

            // Re-convert on Livewire navigation and restart observer for new DOM
            document.addEventListener('livewire:navigated', () => {
                this.convertAllPrices();
                // Restart observer for the new page content
                this._watchForNewPrices();
            });

            // Dispatch ready event
            window.dispatchEvent(new CustomEvent('tc-currency-ready', {
                detail: { code: this.currency }
            }));
        },

        // -------------------------------------------------------------------------
        // PUBLIC API
        // -------------------------------------------------------------------------

        /**
         * Format a price in the current currency
         * @param {number|string} amount - Price in base currency
         * @returns {string} Formatted price (e.g., "USD$150.00")
         */
        format(amount) {
            if (amount === null || amount === undefined || amount === '') {
                return '';
            }

            const num = parseFloat(amount);
            if (isNaN(num)) return '';

            const rate = parseFloat(this.rateData?.exchange_rate) || 1;
            const decimals = this.config?.decimal ?? 2;
            const shouldRound = this.config?.decimalRound || false;

            // Convert
            let converted = num * rate;

            // Round or truncate
            if (shouldRound) {
                converted = Math.round(converted);
            } else {
                converted = truncateNumber(converted, decimals);
            }

            // Format number
            const decimalSep = this.rateData?.decimal_separator || this.config?.decimalSeparator || '.';
            const thousandSep = this.rateData?.thousand_separator || this.config?.thousandSeparator || ',';
            const formatted = formatNumber(converted, shouldRound ? 0 : decimals, decimalSep, thousandSep);

            // Apply currency symbol/code
            return this._applyFormat(formatted);
        },

        /**
         * Convert amount to current currency (number only, no formatting)
         * @param {number|string} amount - Price in base currency
         * @returns {number} Converted amount
         */
        convert(amount) {
            if (amount === null || amount === undefined) return 0;

            const num = parseFloat(amount);
            if (isNaN(num)) return 0;

            const rate = parseFloat(this.rateData?.exchange_rate) || 1;
            return num * rate;
        },

        /**
         * Change the active currency
         * This is the ONLY way to change currency - ensures everything stays in sync
         * @param {string} code - Currency code (e.g., 'EUR')
         */
        setCurrency(code) {
            if (!code || code === this.currency) return;
            if (!this._isValidCurrency(code)) return;

            // Update state
            this.currency = code;
            this.rateData = this._getRateData(code);

            // Update cookie (source of truth)
            setCookie("currency_code", code);

            // Re-render all prices
            this.convertAllPrices();

            // Notify all listeners
            window.dispatchEvent(new CustomEvent('tc-currency-changed', {
                detail: {
                    code: code,
                    rateData: this.rateData
                }
            }));
        },

        /**
         * Get current currency code
         * @returns {string}
         */
        getCurrency() {
            return this.currency;
        },

        /**
         * Get format info for Alpine components that need manual formatting
         * @returns {Object}
         */
        getFormatInfo() {
            return {
                format: this._getFormatString(),
                exchangeRate: parseFloat(this.rateData?.exchange_rate) || 1,
                decimalSeparator: this.rateData?.decimal_separator || this.config?.decimalSeparator || '.',
                thousandSeparator: this.rateData?.thousand_separator || this.config?.thousandSeparator || ',',
                decimal: this.config?.decimal ?? 2,
                decimalRound: this.config?.decimalRound || false
            };
        },

        /**
         * Convert all [data-tc-price] elements on the page
         */
        convertAllPrices() {
            // Pause observer to prevent infinite loop (our DOM changes would trigger it again)
            if (this._observer) {
                this._observer.disconnect();
            }

            document.querySelectorAll("[data-tc-price]").forEach(el => {
                const basePrice = el.getAttribute("data-tc-price");
                if (basePrice !== null && basePrice !== "") {
                    el.textContent = this.format(basePrice);
                    // Prevent Google Translate from translating prices
                    el.classList.add("notranslate");
                    // Remove loading class once price is set
                    el.classList.remove("tc-price-loading");
                }
            });

            // Resume observer for new dynamic content
            if (this._observer) {
                this._observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            }
        },

        // -------------------------------------------------------------------------
        // PRIVATE METHODS
        // -------------------------------------------------------------------------

        /**
         * Check if a currency code is valid (exists in config)
         */
        _isValidCurrency(code) {
            if (code === this.config?.baseCurrency) return true;
            return this.config?.exchangeRates?.some(r => r.currency_code === code) || false;
        },

        /**
         * Get rate data for a currency code
         */
        _getRateData(code) {
            // Base currency
            if (code === this.config?.baseCurrency) {
                return {
                    currency_code: this.config.baseCurrency,
                    symbol: this.config.baseSymbol,
                    exchange_rate: 1,
                    position: null,
                    decimal_separator: this.config.decimalSeparator,
                    thousand_separator: this.config.thousandSeparator
                };
            }

            // Find in exchange rates
            return this.config?.exchangeRates?.find(r => r.currency_code === code) || null;
        },

        /**
         * Build the format string for current currency
         */
        _getFormatString() {
            const symbol = this.rateData?.symbol || this.config?.baseSymbol || '$';
            const code = this.rateData?.currency_code || this.config?.baseCurrency || 'USD';
            const position = this.rateData?.position;

            // Position-based formats
            const formats = {
                'left': `${code}${symbol}:amount`,
                'right': `:amount${code}${symbol}`,
                'left-space': `${code}${symbol} :amount`,
                'right-space': `:amount ${code}${symbol}`
            };

            if (position && formats[position]) {
                return formats[position];
            }

            // Default format
            let format = this.config?.displayFormat || '{currency_symbol}:amount';
            return format
                .replace('{currency_code}', code)
                .replace('{currency_symbol}', symbol)
                .replace('{amount}', ':amount');
        },

        /**
         * Apply format string to a formatted number
         */
        _applyFormat(formattedNumber) {
            return this._getFormatString().replace(':amount', formattedNumber);
        },

        /**
         * Watch for dynamically added price elements
         */
        _watchForNewPrices() {
            // Disconnect existing observer to prevent memory leaks
            this._disconnectObserver();

            this._observer = new MutationObserver(() => {
                clearTimeout(this._observerTimeout);
                // 150ms debounce to coalesce rapid DOM changes (e.g., Livewire updates)
                this._observerTimeout = setTimeout(() => this.convertAllPrices(), 150);
            });

            this._observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        },

        /**
         * Disconnect the MutationObserver and clear timeout
         * Call this when cleaning up to prevent memory leaks
         */
        _disconnectObserver() {
            if (this._observer) {
                this._observer.disconnect();
                this._observer = null;
            }
            if (this._observerTimeout) {
                clearTimeout(this._observerTimeout);
                this._observerTimeout = null;
            }
        },

        /**
         * Clean up resources (call on page unload or SPA navigation)
         */
        destroy() {
            this._disconnectObserver();
            this.ready = false;
        }
    };

    // =============================================================================
    // ALPINE.JS INTEGRATION
    // =============================================================================

    document.addEventListener('alpine:init', () => {
        if (typeof Alpine === 'undefined') return;

        // $currency magic helper
        Alpine.magic('currency', () => ({
            format: (amount) => TCCurrency.format(amount),
            convert: (amount) => TCCurrency.convert(amount),
            setCurrency: (code) => TCCurrency.setCurrency(code),
            getCurrency: () => TCCurrency.getCurrency(),
            getFormatInfo: () => TCCurrency.getFormatInfo()
        }));

        // Reactive store for components that need to trigger re-renders
        Alpine.store('currency', {
            code: null,
            version: 0, // Increment to trigger reactivity

            init() {
                // Sync with TCCurrency on ready - also increment version to trigger re-render
                window.addEventListener('tc-currency-ready', (e) => {
                    this.code = e.detail.code;
                    this.version++; // Trigger re-render with correct currency
                });

                // Update on currency change
                window.addEventListener('tc-currency-changed', (e) => {
                    this.code = e.detail.code;
                    this.version++; // Triggers Alpine reactivity
                });

                // If TCCurrency is already ready, sync now
                if (TCCurrency.ready) {
                    this.code = TCCurrency.currency;
                    this.version++; // Trigger initial re-render
                }
            }
        });
    });

    // =============================================================================
    // GLOBAL EXPOSURE
    // =============================================================================

    window.TCCurrency = TCCurrency;

    // Initialize when config is available
    function tryInit() {
        if (window.TCCurrencyConfig) {
            TCCurrency.init(window.TCCurrencyConfig);
            return true;
        }
        return false;
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', tryInit);
    } else if (!tryInit()) {
        // Config not yet available - retry briefly in case it loads after this script
        let retries = 0;
        const maxRetries = 10;
        const retryInterval = setInterval(() => {
            if (tryInit() || ++retries >= maxRetries) {
                clearInterval(retryInterval);
            }
        }, 50);
    }

})();
